import { useState } from "react";
import { Alert, Button, Card, Form, Input, Space, Typography } from "antd";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "../api/client";
import { palette } from "../theme";
import { BrandBlock, LangSwitch } from "./LoginPage";
import { useIsMobile } from "../components/useIsMobile";
import { PasswordField, usePasswordRules } from "../components/PasswordField";

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const { token = "" } = useParams();
  const nav = useNavigate();
  const mobile = useIsMobile();
  const rules = usePasswordRules();
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);

  const submit = async (v: { password: string }) => {
    setBusy(true);
    setErr(null);
    try {
      await api.post("/auth/reset-password", { token, password: v.password });
      setOk(true);
      setTimeout(() => nav("/login", { replace: true }), 2500);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t("a_loginFail"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: palette.bg, padding: mobile ? 12 : 16 }}>
      <Card style={{ width: "100%", maxWidth: 460 }} styles={{ body: { padding: mobile ? 20 : 28 } }}>
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <BrandBlock />
            <LangSwitch />
          </div>
          <div>
            <Typography.Title level={3} style={{ margin: 0, color: palette.heading }}>{t("c_resetTitle")}</Typography.Title>
            <Typography.Text type="secondary">{t("c_resetSub")}</Typography.Text>
          </div>
          {err && (
            <Alert type="error" showIcon message={err} action={<Link to="/forgot">{t("c_forgotLink")}</Link>} />
          )}
          {ok ? (
            <Alert type="success" showIcon message={t("c_resetDone")} />
          ) : (
            <Form layout="vertical" onFinish={submit} requiredMark={false} disabled={busy}>
              <Form.Item name="password" label={t("c_secPwdNew")} rules={rules}>
                <PasswordField autoFocus />
              </Form.Item>
              <Form.Item
                name="confirm"
                label={t("a_passwordConfirm")}
                dependencies={["password"]}
                rules={[
                  { required: true, message: t("required") },
                  ({ getFieldValue }) => ({ validator: (_, val) => (!val || getFieldValue("password") === val ? Promise.resolve() : Promise.reject(new Error(t("a_pwdMismatch")))) }),
                ]}
              >
                <Input.Password autoComplete="new-password" size="large" style={{ fontSize: 16 }} />
              </Form.Item>
              <Button type="primary" htmlType="submit" block size="large" loading={busy} style={{ height: 48 }}>{t("c_resetGo")}</Button>
            </Form>
          )}
          <div style={{ textAlign: "center" }}><Link to="/login">{t("c_backLogin")}</Link></div>
        </Space>
      </Card>
    </div>
  );
}
